import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { TreetopPropertiesProvider } from 'shared/util-common';

type ExportGuidelineToExternalResultDto = {
  success: boolean;
  fileNames: string[];
  errorMessage?: string;
};

export type ExportGuidelineToExternalResult = {
  success: boolean;
  fileNames: string[];
  errorMessage?: string;
};

const mapDtoToExportGuidelineToExternalResult = (dto: ExportGuidelineToExternalResultDto): ExportGuidelineToExternalResult => dto;

@Injectable({ providedIn: 'root' })
export class GuidelinePdfHttpService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api/export`;

  public exportFlowchartToPdf(
    guidelineUniqueId: string,
    flowchartWidgetId: string,
    flowchartImage: Blob,
    version?: string | null,
  ): Observable<Blob> {
    const formData = new FormData();
    formData.append('flowchartImage', flowchartImage, 'flowchart.png');

    const params: Record<string, string> = {};
    if (version) {
      params['version'] = version;
    }

    return this.http.post(`${this.baseUrl}/${guidelineUniqueId}/flowchart-widgets/${flowchartWidgetId}/pdf`, formData, {
      responseType: 'blob',
      params,
    });
  }

  public exportGuidelineToPdf(
    guidelineUniqueId: string,
    flowchartImages: Map<string, Blob>,
    referenceNumbers: Record<string, number> = {},
    version?: string | null,
  ): Observable<Blob> {
    const formData = new FormData();

    flowchartImages.forEach((blob, widgetId) => {
      formData.append(`flowchartImages`, blob, `${widgetId}.png`);
    });

    formData.append('referenceNumbers', JSON.stringify(referenceNumbers));

    const params: Record<string, string> = {};
    if (version) {
      params['version'] = version;
    }

    return this.http.post(`${this.baseUrl}/guidelines/${guidelineUniqueId}/pdf`, formData, { responseType: 'blob', params });
  }

  public exportGuidelineToHtml(
    guidelineUniqueId: string,
    flowchartImages: Map<string, Blob>,
    referenceNumbers: Record<string, number> = {},
    version?: string | null,
  ): Observable<Blob> {
    const formData = new FormData();

    flowchartImages.forEach((blob, widgetId) => {
      formData.append('flowchartImages', blob, `${widgetId}.png`);
    });

    formData.append('referenceNumbers', JSON.stringify(referenceNumbers));

    const params: Record<string, string> = {};
    if (version) {
      params['version'] = version;
    }

    return this.http.post(`${this.baseUrl}/guidelines/${guidelineUniqueId}/html`, formData, { responseType: 'blob', params });
  }

  public exportGuidelineToExternal(
    guidelineUniqueId: string,
    flowchartImages: Map<string, Blob>,
    referenceNumbers: Record<string, number> = {},
  ): Observable<ExportGuidelineToExternalResult> {
    const formData = new FormData();

    flowchartImages.forEach((blob, widgetId) => {
      formData.append('flowchartImages', blob, `${widgetId}.png`);
    });

    formData.append('referenceNumbers', JSON.stringify(referenceNumbers));

    return this.http
      .post<ExportGuidelineToExternalResultDto>(`${this.baseUrl}/guidelines/${guidelineUniqueId}/upload-to-external`, formData)
      .pipe(map(mapDtoToExportGuidelineToExternalResult));
  }
}
